import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { StatCard, ProgressBar, StatusBadge } from "@/components/domain/Bits";
import { Button } from "@/components/ui/button";
import { money, num, fmtDateTime, PAYMENT_STATUS } from "@/lib/format";
import { DollarSign, Target, TrendingUp, Users, Plus, Share2, Handshake, Receipt } from "lucide-react";
import { toast } from "sonner";

export default function SellerDashboard() {
  const [s, setS] = useState(null);
  const [sales, setSales] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    api.get("/seller/summary").then((r) => setS(r.data)).catch(() => {});
    api.get("/seller/sales").then((r) => setSales(r.data)).catch(() => {});
    api.get("/seller/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const code = s?.code || "";
  const link = code ? `${window.location.origin}/sorteos?v=${code}` : "";
  const goal = s?.goal || 0;
  const salesMonth = s?.sales_month || 0;
  const progress = goal ? Math.min(100, Math.round((salesMonth / goal) * 100)) : 0;
  const level = s?.level;
  const schemeLabel = s?.commission_type === "per_ticket" ? `${money(s?.commission_per_ticket || 0)} por boleto`
    : s?.commission_type === "tiered" ? `por niveles · actualmente ${Math.round((level?.current?.rate || 0) * 100)}%`
    : `${Math.round((s?.commission_rate || 0) * 100)}% sobre ventas`;

  return (
    <AdminLayout title="Portal del vendedor">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Ventas del mes" value={num(salesMonth)} icon={TrendingUp} />
        <StatCard label="Meta mensual" value={num(goal)} icon={Target} />
        <StatCard label="Comisión ganada" value={money(s?.commission_earned || 0)} tone="success" icon={DollarSign} />
        <StatCard label="Conversión" value={`${s?.conversion || 0}%`} icon={Users} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="seller-goal-card">
          <div className="flex items-center justify-between"><h3 className="font-semibold text-slate-900">Progreso hacia tu meta</h3><span className="font-mono text-sm font-semibold text-brand">{progress}%</span></div>
          <div className="mt-4"><ProgressBar value={progress} /></div>
          <div className="mt-3 text-sm text-slate-500">{goal ? <>Te faltan <span className="font-mono font-semibold text-slate-900">{Math.max(0, goal - salesMonth)}</span> boletos para {level?.next ? `desbloquear el nivel ${level.level + 1} (${Math.round(level.next.rate * 100)}% de comisión)` : "alcanzar tu objetivo"}.</> : level ? "¡Alcanzaste el nivel máximo este mes!" : "Tu administrador aún no define una meta."}</div>
          {level && (
            <div className="mt-4 grid gap-2 sm:grid-cols-3" data-testid="seller-levels">
              {level.tiers.map((t, i) => {
                const reached = level.tickets_month >= t.tickets;
                const isCur = level.current && level.current.tickets === t.tickets;
                return (
                  <div key={t.tickets} className={`rounded-lg border p-3 text-center ${isCur ? "border-brand bg-brand/5" : reached ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-testid={`seller-level-${i}`}>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Nivel {i + 1}</div>
                    <div className="font-mono text-lg font-bold text-slate-900">{Math.round(t.rate * 100)}%</div>
                    <div className="text-xs text-slate-500">desde {num(t.tickets)} boletos</div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <Button asChild className="rounded-full bg-brand hover:bg-brand-hover" data-testid="seller-new-sale"><Link to="/sorteos"><Plus className="mr-1.5 h-4 w-4" />Nueva venta</Link></Button>
            <Button variant="outline" className="rounded-full" disabled={!link} onClick={() => { navigator.clipboard.writeText(link); toast.success("Enlace copiado"); }} data-testid="seller-share"><Share2 className="mr-1.5 h-4 w-4" />Copiar enlace atribuido</Button>
          </div>
          {link && <div className="mt-4 rounded-lg bg-slate-50 p-3 font-mono text-xs text-slate-500 break-all" data-testid="seller-link">{link}</div>}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="seller-commission-card">
          <h3 className="flex items-center gap-2 font-semibold text-slate-900"><Handshake className="h-4 w-4 text-brand" />Comisiones</h3>
          <div className="mt-4 space-y-2">
            {[
              ["Ganada (pagos aprobados)", money(s?.commission_earned || 0), "bg-emerald-50 text-emerald-700"],
              ["Por confirmar (pagos en proceso)", money(s?.commission_pending || 0), "bg-amber-50 text-amber-700"],
              ["Ingresos generados", money(s?.revenue || 0), "bg-blue-50 text-blue-700"],
            ].map(([l, v, cls]) => (
              <div key={l} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5">
                <span className="text-sm text-slate-600">{l}</span><span className={`rounded-full px-2.5 py-1 font-mono text-sm font-bold ${cls}`}>{v}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-400">Esquema de comisión: {schemeLabel}. Solo ves tus propias operaciones; el aislamiento se valida en el servidor.</p>
        </div>
      </div>

      {/* Ventas */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="seller-sales-section">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4"><Receipt className="h-4 w-4 text-brand" /><h3 className="font-semibold text-slate-900">Mis ventas</h3><span className="ml-auto text-xs text-slate-400">{sales.length} operaciones</span></div>
        {sales.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-400">Aún no tienes ventas atribuidas. Comparte tu enlace para empezar.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
                <tr><th className="px-5 py-3">Folio</th><th className="px-5 py-3">Cliente</th><th className="px-5 py-3">Sorteo</th><th className="px-5 py-3 text-right">Boletos</th><th className="px-5 py-3 text-right">Total</th><th className="px-5 py-3">Estado</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sales.map((r) => (
                  <tr key={r.folio} data-testid={`seller-sale-${r.folio}`}>
                    <td className="px-5 py-3 font-mono text-xs text-slate-500">{r.folio}</td>
                    <td className="px-5 py-3 font-medium text-slate-900">{r.customer_name}</td>
                    <td className="px-5 py-3 text-slate-600">{r.raffle_name}</td>
                    <td className="px-5 py-3 text-right font-mono">{num(r.tickets)}</td>
                    <td className="px-5 py-3 text-right font-mono">{money(r.total)}</td>
                    <td className="px-5 py-3"><StatusBadge {...(PAYMENT_STATUS[r.payment_status] || { label: r.payment_status, cls: "bg-slate-100 text-slate-600" })} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Clientes */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="seller-clients-section">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4"><Users className="h-4 w-4 text-brand" /><h3 className="font-semibold text-slate-900">Mis clientes</h3><span className="ml-auto text-xs text-slate-400">{clients.length} clientes</span></div>
        {clients.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-400">Todavía no hay clientes atribuidos a tu código.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
                <tr><th className="px-5 py-3">Cliente</th><th className="px-5 py-3">Contacto</th><th className="px-5 py-3 text-right">Reservas</th><th className="px-5 py-3 text-right">Boletos</th><th className="px-5 py-3 text-right">Gastado</th><th className="px-5 py-3">Última compra</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {clients.map((c, i) => (
                  <tr key={c.email || c.whatsapp || i} data-testid={`seller-client-${i}`}>
                    <td className="px-5 py-3 font-medium text-slate-900">{c.name}</td>
                    <td className="px-5 py-3 text-xs text-slate-500">{c.email || c.whatsapp || "—"}</td>
                    <td className="px-5 py-3 text-right font-mono">{num(c.reservations)}</td>
                    <td className="px-5 py-3 text-right font-mono">{num(c.tickets)}</td>
                    <td className="px-5 py-3 text-right font-mono">{money(c.spent)}</td>
                    <td className="px-5 py-3 text-xs text-slate-500">{c.last_purchase ? fmtDateTime(c.last_purchase) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
